const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const WALL_CLOCK_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/;

function toDate(value: Date | string | number): Date {
    if (value instanceof Date) {
        return value;
    }
    return new Date(value);
}

function partMap(parts: Intl.DateTimeFormatPart[]): Record<string, string> {
    const map: Record<string, string> = {};
    for (const part of parts) {
        if (part.type !== "literal") {
            map[part.type] = part.value;
        }
    }
    return map;
}

/**
 * Convert GraphQL TimezoneEnum values such as EuropeStockholm or AmericaNew_York to IANA names.
 */
export function timezoneEnumToIana(timezoneEnum: string): string {
    return timezoneEnum.replace(/^([A-Z][a-z]+)(.+)$/, "$1/$2");
}

export function formatYmdInTimeZone(value: Date | string | number, timeZone: string): string {
    return new Intl.DateTimeFormat("en-SE", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).format(toDate(value));
}

export function formatDateTimeLocal(value: Date | string | number): string {
    const date = toDate(value);
    const ymd = new Intl.DateTimeFormat("en-SE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).format(date);
    const hms = new Intl.DateTimeFormat("en-SE", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23"
    }).format(date);
    return `${ymd} ${hms}`;
}

export function formatYmdLocal(value: Date | string | number): string {
    return new Intl.DateTimeFormat("en-SE", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).format(toDate(value));
}

export function formatWeekdayLong(value: Date | string | number): string {
    return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(toDate(value));
}

function normalizeOffset(timeZoneName: string): string {
    let offset = timeZoneName.replace(/^GMT/, "").replace(/^UTC/, "");
    if (!offset || offset === "Z") {
        return "+00:00";
    }
    if (/^[+-]\d$/.test(offset)) {
        offset = `${offset[0]}0${offset.slice(1)}:00`;
    } else if (/^[+-]\d{2}$/.test(offset)) {
        offset = `${offset}:00`;
    } else if (/^[+-]\d:\d{2}$/.test(offset)) {
        offset = `${offset[0]}0${offset.slice(1)}`;
    } else if (/^[+-]\d{4}$/.test(offset)) {
        offset = `${offset.slice(0, 3)}:${offset.slice(3)}`;
    }
    return offset;
}

export function formatIsoOffsetInTimeZone(value: Date | string | number, timeZone: string): string {
    const date = toDate(value);
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        fractionalSecondDigits: 3,
        timeZoneName: "longOffset"
    }).formatToParts(date);
    const map = partMap(parts);
    let hour = map.hour ?? "00";
    if (hour === "24") {
        hour = "00";
    }
    const fractional = (map.fractionalSecond ?? String(date.getUTCMilliseconds()).padStart(3, "0")).padEnd(3, "0");
    const offset = normalizeOffset(map.timeZoneName ?? "GMT+00:00");
    return `${map.year}-${map.month}-${map.day}T${hour}:${map.minute}:${map.second}.${fractional}${offset}`;
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone,
        hourCycle: "h23",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
    }).formatToParts(date);
    const map = partMap(parts);
    let hour = Number(map.hour);
    if (hour === 24) {
        hour = 0;
    }
    const asUtc = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), hour, Number(map.minute), Number(map.second));
    return asUtc - date.getTime();
}

/**
 * Interpret a wall-clock date/time in `timeZone` as a UTC Date.
 * Date instances and ISO strings with Z/offset are treated as absolute instants.
 */
export function zonedTimeToUtc(value: Date | string | number, timeZone: string): Date {
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === "number") {
        return new Date(value);
    }

    const str = String(value).trim();
    if (/[zZ]$/.test(str) || /[+-]\d{2}:\d{2}$/.test(str) || /[+-]\d{4}$/.test(str)) {
        return new Date(str);
    }

    const match = str.match(WALL_CLOCK_RE);
    if (!match) {
        return new Date(str);
    }

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const hour = Number(match[4] ?? 0);
    const minute = Number(match[5] ?? 0);
    const second = Number(match[6] ?? 0);
    const ms = Number((match[7] ?? "0").padEnd(3, "0"));
    const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, ms);
    const offset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone);
    const result = new Date(utcGuess - offset);
    const offset2 = getTimeZoneOffsetMs(result, timeZone);
    if (offset !== offset2) {
        return new Date(utcGuess - offset2);
    }
    return result;
}

export function addDaysToDate(value: Date | string | number, days: number): Date {
    const date = toDate(value);
    const result = new Date(date.getTime());
    result.setDate(result.getDate() + days);
    return result;
}

export function addDaysYmd(value: Date | string | number, days: number): string {
    return formatYmdLocal(addDaysToDate(value, days));
}

export function addDaysYmdUtc(ymd: string, days: number): string {
    const match = ymd.match(YMD_RE);
    if (!match) {
        return new Date(NaN).toISOString();
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const dt = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    dt.setUTCDate(dt.getUTCDate() + days);
    return dt.toISOString().slice(0, 10);
}

export function startOfZonedDayUtc(value: Date | string | number, timeZone: string): Date {
    const ymd = formatYmdInTimeZone(value, timeZone);
    return zonedTimeToUtc(ymd, timeZone);
}

export function yesterdayYmd(): string {
    return addDaysYmd(new Date(), -1);
}

export function daysFromTodayCount(date: Date | string | number): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (today.getTime() - toDate(date).getTime()) / 86400000;
}

export function parseYmdUtc(ymd: string): Date {
    const match = ymd.substring(0, 10).match(YMD_RE);
    if (!match) {
        return new Date(NaN);
    }
    return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
}

export function calendarDaysBetweenYmd(fromYmd: string, toYmd: string): number {
    return (parseYmdUtc(toYmd).getTime() - parseYmdUtc(fromYmd).getTime()) / 86400000;
}

export function addMonthsUtc(date: Date, months: number): Date {
    const result = new Date(date.getTime());
    result.setUTCMonth(result.getUTCMonth() + months);
    return result;
}

export function endOfMonthUtc(date: Date): Date {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
}

export function endOfQuarterUtc(date: Date): Date {
    const quarterEndMonth = Math.floor(date.getUTCMonth() / 3) * 3 + 2;
    return new Date(Date.UTC(date.getUTCFullYear(), quarterEndMonth + 1, 0));
}

export function previousAccountingPeriodYear(date: string, frequency: string): string {
    const parsed = parseYmdUtc(date.substring(0, 10));
    if (frequency === "Quarterly") {
        const previous = addMonthsUtc(parsed, -3);
        return String(endOfQuarterUtc(previous).getUTCFullYear());
    }
    if (frequency === "Monthly") {
        const previous = addMonthsUtc(parsed, -1);
        return String(endOfMonthUtc(previous).getUTCFullYear());
    }
    console.log("Please specify a valid accounting frequency on party instead of " + frequency);
    return "Please specify a valid accounting frequency on party instead of " + frequency;
}
